import AutoGenIconUrl from "src/assets/icons/auto-gen-white.svg?url";

import PDialogForm from "src/components/PDialogForm.vue";

import { useQuasar } from "quasar";
import { numberFormat } from "src/assets/scripts/utilities/helpers";

export function useOneClickProcess({
  selectedPRs,
  selectedPR,
  closeForm,
  isLoading,
  totalNetPayable,
  label = "1-Click Process Payment",
  componentProps = {},
  resetSelection,
}) {
  const $q = useQuasar();

  const { canOneClickProcessEdit } = usePaymentPermissions();

  const { oneClickBulkProcess } = usePaymentRequestStore();

  const computedOneClickProcess = computed(() => {
    if (unref(selectedPR)) {
      const max_transfer_limit_per_24_hours =
        unref(selectedPR)?.oneClickProcess?.max_transfer_limit_per_24_hours;
      const totalAmount = unref(selectedPR)?.oneClickProcess?.totalAmount;
      const remainingLimit = Math.max(
        max_transfer_limit_per_24_hours - totalAmount,
        0,
      );

      return {
        allowed: Boolean(
          unref(selectedPR)?.oneClickProcess
            ?.isCurrentAmountLessThanMaxAllowedAmount,
        ),
        totalAmount,
        remainingLimit,
        max_transfer_limit_per_24_hours,
        extraNote: `The total amount exceeds your available transfer limit of ₹ ${remainingLimit} from your daily limit of ₹ ${max_transfer_limit_per_24_hours}. Please contact support for assistance`,
      };
    }

    if (unref(selectedPRs)?.length && unref(selectedPRs)[0]?.oneClickProcess) {
      const max_transfer_limit_per_24_hours =
        unref(selectedPRs)[0]?.oneClickProcess?.max_transfer_limit_per_24_hours;
      const totalAmount = unref(selectedPRs)[0]?.oneClickProcess?.totalAmount;

      const remainingLimit = Math.max(
        max_transfer_limit_per_24_hours - totalAmount,
        0,
      );

      const sumNetPayable = unref(selectedPRs).reduce(
        (sum, pr) => sum + +pr.net_payable,
        0,
      );

      return {
        allowed: Boolean(
          +sumNetPayable + totalAmount <= +max_transfer_limit_per_24_hours,
        ),
        totalAmount,
        remainingLimit,
        max_transfer_limit_per_24_hours,
        extraNote: `The total amount exceeds your available transfer limit of ₹ ${remainingLimit} from your daily limit of ₹ ${max_transfer_limit_per_24_hours}. Please contact support for assistance`,
      };
    }

    return {
      allowed: false,
      totalAmount: 0,
      max_transfer_limit_per_24_hours: 0,
      remainingLimit: 0,
      extraNote: `The total amount exceeds your available transfer limit of ₹ ${0} from your daily limit of ₹ ${0}. Please contact support for assistance`,
    };
  });

  const paymentRequests = computed(() => {
    const pr = {
      pr_internal_numbers: [],
      pr_ids: [],
    };

    if (unref(selectedPRs)?.length) {
      pr.pr_internal_numbers = unref(selectedPRs).map(
        (pr) => pr.internal_number,
      );

      pr.pr_ids = unref(selectedPRs).map((pr) => pr._id);

      return pr;
    }

    if (unref(selectedPR)) {
      pr.pr_internal_numbers = [unref(selectedPR)?.internal_number];

      pr.pr_ids = [unref(selectedPR)._id];
    }

    return pr;
  });

  const confirmationHandler = async (confirmationDialog, props) => {
    try {
      isLoading.value = true;

      const payload = {
        remarks: unref(props.remarks),
        transfer_bank_id: unref(props.transfer_bank_id),
        otp: unref(props.otp),
        pr_internal_numbers: unref(paymentRequests).pr_internal_numbers,
        pr_ids: unref(paymentRequests).pr_ids,
      };

      const response = await oneClickBulkProcess(payload);

      if (response) {
        confirmationDialog?.hide();

        resetSelection?.();

        closeForm();
      }
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  const payload = computed(() => {
    let pr_internal_numbers = "";

    pr_internal_numbers =
      unref(paymentRequests)?.pr_internal_numbers.join(", ");

    const payload = {
      pr_internal_numbers,
    };

    return payload;
  });

  const onConfirm = () => {
    let text = "Are you sure want to Process Payment below?";

    if (unref(selectedPRs)?.length) {
      text = `A total amount of <span class="text-weight-medium text-bold">${numberFormat({ number: unref(totalNetPayable), style: "currency" })}</span> will be initiated for transfer across <span class="text-weight-bold text-bold"> ${unref(selectedPRs).length}</span> vendor bank accounts.<br><span class="text-weight-medium text-bold">Do you confirm to proceed with the transfers?</span>`;
    }

    const confirmationDialog = $q.dialog({
      component: PDialogForm,
      componentProps: {
        cancellable: false,
        loading: isLoading,
        textClass: "text-md text-neutral-80 text-weight-regular",
        submitLabel: "Approve Transfer",
        form: {
          input: {
            type: "textarea",
            autogrow: true,
            placeholder: "Enter transfer remark",
            label: "Transfer Remarks",
            labelClasses:
              "text-sm text-neutral-100 text-weight-regular  q-mt-sm",
          },
          dropdown: {
            "option-label": "displayName",
            "option-value": "_id",
            "input-debounce": "300",
            "virtual-scroll-slice-size": "25",
            "virtual-scroll-item-size": "53",
            placeholder: "Select a Bank Account",

            label: "Select the bank",
            labelClasses: "text-sm text-neutral",

            help: "Poshn’s bank account to transfer amount",
            helpClasses: "text-sm text-neutral-70 q-mt-none",
            filter: bankFilter,
            validations: {
              label: "Select the bank",
              required: true,
            },
          },
          otpComponent: {
            payload: unref(payload),
          },
        },
        onConfirm: (remarks, transfer_bank_id, otp) => {
          confirmationHandler(confirmationDialog, {
            remarks,
            transfer_bank_id,
            otp,
          });
        },
        text,
        ...componentProps,
      },
    });
  };

  const oneClickProcessButton = computed(() => {
    return {
      label: label,
      icon: `img:${AutoGenIconUrl}`,
      onClick: onConfirm,
      disable:
        !unref(canOneClickProcessEdit) ||
        unref(totalNetPayable) <= 0 ||
        !unref(computedOneClickProcess).allowed,
      type: "button",
    };
  });

  const bankFilter = useFilter({
    entity: "bank",
    options: { params: { applies_to: "poshn", bank_name: "ICICI" } },
  });

  return {
    oneClickProcessButton,
    computedOneClickProcess,
  };
}
